import { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileText,
  Download,
  Loader2,
  FolderArchive,
  FileCheck,
  RefreshCw,
} from 'lucide-react';
import {
  bulkUploadToStorage,
  listUploads,
  downloadUpload,
  bulkParseUploads,
  exportBinder,
  type UploadRecord,
} from '../services/api';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function BulkStorage() {
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listUploads(100);
      setUploads(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await bulkUploadToStorage(acceptedFiles);
      setSuccess(`Uploaded ${res.count} file(s)`);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [refresh]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'], 'application/pdf': ['.pdf'] },
    multiple: true,
    disabled: uploading,
  });

  const handleDownload = async (id: string, name: string) => {
    try {
      const blob = await downloadUpload(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'download';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    }
  };

  const handleParseAll = async () => {
    setParsing(true);
    setError(null);
    try {
      const res = await bulkParseUploads();
      setSuccess(`Parsed ${res.parsed}, errors: ${res.errors}`);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const handleCreateBinder = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Select at least one file');
      return;
    }
    setExporting(true);
    setError(null);
    try {
      const blob = await exportBinder(ids, 'zip', true);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `binder-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccess(`Downloaded binder (${ids.length} files)`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === uploads.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(uploads.map(u => u.id)));
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Bulk Upload & Binder</h1>
        <p className="text-white/70 mt-1">Upload PDFs/images, parse text, and export as ZIP with originals + manifest.</p>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors
          ${isDragActive ? 'border-amber-400 bg-amber-500/10' : 'border-white/30 bg-white/5 hover:bg-white/10'}
          ${uploading ? 'opacity-70 pointer-events-none' : 'cursor-pointer'}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <Loader2 className="w-12 h-12 mx-auto text-amber-400 animate-spin" />
        ) : (
          <Upload className="w-12 h-12 mx-auto text-white/60" />
        )}
        <p className="mt-2 text-white/80">
          {uploading ? 'Uploading...' : isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
        </p>
        <p className="text-sm text-white/50 mt-1">PDF, images (PNG, JPG, GIF, WebP)</p>
      </div>

      {/* Messages */}
      {error && <div className="p-3 bg-red-500/20 text-red-200 rounded-lg text-sm">{error}</div>}
      {success && <div className="p-3 bg-emerald-500/20 text-emerald-200 rounded-lg text-sm">{success}</div>}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleParseAll}
          disabled={parsing || uploads.filter(u => u.status === 'stored').length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white disabled:opacity-50"
        >
          {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
          Parse All Stored
        </button>
        <button
          onClick={handleCreateBinder}
          disabled={exporting || selectedIds.size === 0}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500/80 hover:bg-amber-500 text-shuttle-dark font-medium rounded-lg disabled:opacity-50"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderArchive className="w-4 h-4" />}
          Create Binder ({selectedIds.size} selected)
        </button>
        <button onClick={refresh} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/20 overflow-hidden bg-white/5">
        {loading ? (
          <div className="p-8 text-center text-white/60"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>
        ) : uploads.length === 0 ? (
          <div className="p-8 text-center text-white/60">No uploads yet. Drop files above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/20 text-left text-white/70">
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === uploads.length && uploads.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Size</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => (
                  <tr key={u.id} className="border-b border-white/10 hover:bg-white/5">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        onChange={() => toggleSelect(u.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3 text-white font-mono truncate max-w-[200px]" title={u.original_name}>
                      {u.original_name}
                    </td>
                    <td className="p-3 text-white/70">{formatBytes(u.byte_size)}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${u.status === 'parsed' ? 'bg-emerald-500/30' : u.status === 'error' ? 'bg-red-500/30' : 'bg-white/20'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleDownload(u.id, u.original_name)}
                        className="flex items-center gap-1 text-amber-400 hover:text-amber-300"
                      >
                        <Download className="w-4 h-4" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
